import React from "react";
import { Route, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../redux/reducers";
import { hasRouteAccess } from "../utils/permissions";
import Unauthorized from "../view/errors/Unauthorized";

interface PrivateRouteProps {
  component: React.ComponentType<any>;
  path: string;
  exact?: boolean;
}

/**
 * PrivateRoute component that checks route permissions before rendering
 *
 * Usage:
 * <PrivateRoute exact path="/template" component={Template} />
 *
 * The component will:
 * 1. Check if user has permission to access the route
 * 2. Redirect to unauthorized page if no permission
 * 3. Render the component if permission is granted
 */
export const PrivateRoute: React.FC<PrivateRouteProps> = ({
  component: Component,
  path,
  exact = false,
  ...rest
}) => {
  const user = useSelector((state: RootState) => state.user);

  // Check if user has access to this route
  const hasAccess = hasRouteAccess(user, path);

  if (!hasAccess) {
    // User doesn't have permission - render unauthorized page
    return (
      <Route
        {...rest}
        exact={exact}
        path={path}
        render={() => <Unauthorized />}
      />
    );
  }

  // User has permission - render the component
  return (
    <Route
      {...rest}
      exact={exact}
      path={path}
      render={(props) => <Component {...props} />}
    />
  );
};

export default PrivateRoute;
